import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter as rxFilter } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { AudienceDashboardService } from './audience-engine.service'
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class FilterService {
  private readonly applyFiltersStorageKey = 'audience-engine.applyFiltersToAllCharts';
  private readonly defaultApplyFiltersState = true;

  private selectedAudiencesSubject = new BehaviorSubject<string[]>([]);
  selectedAudiences$ = this.selectedAudiencesSubject.asObservable().pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
  );

  private selectedDateRangeSubject = new BehaviorSubject<'7days' | '14days' | '30days' | '90days' | 'year'>('30days');
  selectedDateRange$ = this.selectedDateRangeSubject.asObservable().pipe(distinctUntilChanged());

  private selectedDevicesSubject = new BehaviorSubject<string[]>([]);
  selectedDevices$ = this.selectedDevicesSubject.asObservable().pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
  );

  private selectedTrafficSourcesSubject = new BehaviorSubject<string[]>([]);
  selectedTrafficSources$ = this.selectedTrafficSourcesSubject.asObservable().pipe(
    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
  );

  private selectedGoalSubject = new BehaviorSubject<string>('');
  selectedGoal$ = this.selectedGoalSubject.asObservable().pipe(distinctUntilChanged());

  private allFilters: any[] = [];
  private dashboardAudiences: any[] = [];
  private personasAudiences: any[] = [];
  private availableAudiences: Array<{ id: string; name: string; color: string; selected: boolean; description: string; type?: string }> = [];
  private audiencesLoaded = false;

  private filtersLoadedSubject = new BehaviorSubject<boolean>(false);
  filtersLoaded$ = this.filtersLoadedSubject.asObservable();

  private applyFiltersToAllChartsSubject = new BehaviorSubject<boolean>(this.loadApplyFiltersPreference());
  applyFiltersToAllCharts$ = this.applyFiltersToAllChartsSubject.asObservable();

  private availableDevices: Array<{ id: string; name: string; selected: boolean }> = [];
  private availableTrafficSources: Array<{ id: string; name: string; selected: boolean }> = [];
  private availableGoals: Array<{ id: string; name: string; selected: boolean }> = [];

  private audienceColors = [
    '#1e3a8a', '#3b82f6', '#fbbf24', '#f97316', '#8b5cf6',
    '#ec4899', '#10b981', '#14b8a6', '#f59e0b', '#ef4444'
  ];
  private readonly paletteSize = 5; // Use first 5 colors alternating
  private inReachContext = false;
  private filtersLoading = false;
  private inVisionContext = false;

  constructor(
    private audienceDashboardService: AudienceDashboardService,
    private authService: AuthService,
    private router: Router
  ) {
    this.inReachContext = this.isReachRoute(this.router.url || '');
    this.inVisionContext = this.isVisionRoute(this.router.url || '');
    this.router.events
      .pipe(rxFilter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const newReachState = this.isReachRoute(event.urlAfterRedirects || event.url);
        const changed = newReachState !== this.inReachContext;
        this.inReachContext = newReachState;
        if (changed && !this.inReachContext) {
          this.fetchFiltersIfNeeded();
        }

        const newVisionState = this.isVisionRoute(event.urlAfterRedirects || event.url);
        const visionChanged = newVisionState !== this.inVisionContext;
        this.inVisionContext = newVisionState;
        if (visionChanged && this.inVisionContext) {
          this.fetchFiltersIfNeeded();
        }
      });

    this.authService.selectedDcr$
      .pipe(distinctUntilChanged())
      .subscribe(dcrId => this.onActiveDcrChange(dcrId));
    // selectedDcr$ e' un BehaviorSubject: la subscribe qui sopra gestisce gia' il DCR corrente.
    // Richiamare onActiveDcrChange() a mano resettava filtersLoading e lanciava una seconda
    // get-filters identica (BigQuery) a ogni bootstrap.
    this.fetchFiltersIfNeeded();

    // On refresh, the initial evaluation above may run against stale localStorage
    // data (e.g. hasVisionAccess not yet correct). changeSelectedEntity() re-syncs
    // currentUser with fresh data but only emits on currentUser$/entitySyncPending$,
    // not on selectedDcr$ - so we must re-check once that sync completes, otherwise
    // fetchFiltersIfNeeded() never runs again and filtersLoaded$ stays false forever.
    this.authService.entitySyncPending$
      .pipe(rxFilter(pending => !pending))
      .subscribe(() => this.fetchFiltersIfNeeded());
  }

  private fetchFilters(): void {
    if (this.filtersLoading || this.shouldSkipVisionRequests()) {
      return;
    }
    this.filtersLoading = true;
    const activeDcrAtRequest = this.authService.getActiveDcr();
    this.audienceDashboardService.getFilters().subscribe({
      next: (response) => {
        this.filtersLoading = false;
        if (activeDcrAtRequest !== this.authService.getActiveDcr()) {
          return;
        }
        if (response.error) {
          console.error('[FILTER SERVICE] Error loading filters:', response.error);
          return;
        }

        const filters = response.payload || [];
        this.allFilters = filters;

        const normalizeType = (raw?: string): string => (raw ?? '').toString().trim().toLowerCase();

        const dashboardFilters = filters.filter(f => normalizeType(f.ObjectType) === 'audience');
        const personasFilters = filters.filter(f => normalizeType(f.ObjectType) === 'personas');
        const deviceFilters = filters.filter(f => normalizeType(f.ObjectType) === 'device');
        const sourceFilters = filters.filter(f => normalizeType(f.ObjectType) === 'source');
        const goalFilters = filters.filter(f => normalizeType(f.ObjectType) === 'goal');

        let colorIndex = 0;

        const uniqueDashboardAudiences = new Map<string, any>();
        dashboardFilters.forEach(filter => {
          if (!uniqueDashboardAudiences.has(filter.ColumnName)) {
            const color = this.audienceColors[colorIndex % this.paletteSize];
            uniqueDashboardAudiences.set(filter.ColumnName, {
              id: filter.ColumnName,
              name: filter.ObjectName,
              color,
              selected: uniqueDashboardAudiences.size < 5,
              description: `Public Audience: ${filter.ObjectName}`,
              type: 'audience'
            });
            colorIndex = (colorIndex + 1) % this.paletteSize;
          }
        });
        this.dashboardAudiences = Array.from(uniqueDashboardAudiences.values());

        const uniquePersonasAudiences = new Map<string, any>();
        personasFilters.forEach(filter => {
          if (!uniquePersonasAudiences.has(filter.ColumnName)) {
            const color = this.audienceColors[colorIndex % this.paletteSize];
            uniquePersonasAudiences.set(filter.ColumnName, {
              id: filter.ColumnName,
              name: filter.ObjectName,
              color,
              selected: uniquePersonasAudiences.size < 5,
              description: `Personas Audience: ${filter.ObjectName}`,
              type: 'personas'
            });
            colorIndex = (colorIndex + 1) % this.paletteSize;
          }
        });
        this.personasAudiences = Array.from(uniquePersonasAudiences.values());

        this.availableDevices = deviceFilters.map(filter => ({
          id: filter.ColumnName,
          name: filter.ObjectName,
          selected: false
        }));

        this.availableTrafficSources = sourceFilters.map(filter => ({
          id: filter.ColumnName,
          name: filter.ObjectName,
          selected: false
        }));

        this.availableGoals = goalFilters.map(filter => ({
          id: filter.ColumnName,
          name: filter.ObjectName,
          selected: false
        }));

        this.syncSelectedDevices();
        this.syncSelectedSources();
        this.syncSelectedGoal();

        this.availableAudiences = this.dashboardAudiences;
        this.audiencesLoaded = true;

        this.syncSelectedAudiences();

        this.filtersLoadedSubject.next(true);
      },
      error: (error) => {
        this.filtersLoading = false;
        if (activeDcrAtRequest !== this.authService.getActiveDcr()) {
          return;
        }
        console.error('[FILTER SERVICE] Error fetching filters:', error);
      }
    });
  }

  private onActiveDcrChange(dcrId: string | null): void {
    this.resetFiltersState();
    if (dcrId || this.authService.hasGlobalDataAccess()) {
      this.fetchFilters();
    }
  }

  private fetchFiltersIfNeeded(): void {
    if (this.shouldSkipVisionRequests()) {
      return;
    }
    if ((this.authService.getActiveDcr() || this.authService.hasGlobalDataAccess()) && !this.audiencesLoaded) {
      this.fetchFilters();
    }
  }

  private shouldSkipVisionRequests(): boolean {
    return this.inReachContext || !this.inVisionContext || !this.authService.currentDcrHasVisionAccess();
  }

  private resetFiltersState(): void {
    this.allFilters = [];
    this.dashboardAudiences = [];
    this.personasAudiences = [];
    this.availableAudiences = [];
    this.availableDevices = [];
    this.availableTrafficSources = [];
    this.availableGoals = [];
    this.audiencesLoaded = false;
    this.filtersLoadedSubject.next(false);
    this.selectedAudiencesSubject.next([]);
    this.selectedDevicesSubject.next([]);
    this.selectedTrafficSourcesSubject.next([]);
    this.selectedGoalSubject.next('');
    this.filtersLoading = false;
  }

  switchAudienceContext(context: 'public' | 'personas'): void {
    this.availableAudiences = context === 'public'
      ? this.dashboardAudiences
      : this.personasAudiences;
    this.syncSelectedAudiences();
  }

  private isVisionRoute(url: string): boolean {
    return (url || '').startsWith('/vision');
  }

  getPublicAudiences(): any[] {
    return this.dashboardAudiences;
  }

  getPersonasAudiences(): any[] {
    return this.personasAudiences;
  }

  getAllFilters(): any[] {
    return this.allFilters;
  }

  getAvailableAudiences(): any[] {
    return this.availableAudiences;
  }

  getSelectedAudiences(): any[] {
    return this.availableAudiences.filter(aud => aud.selected);
  }

  toggleAudience(audienceId: string): void {
    const audience = this.availableAudiences.find(aud => aud.id === audienceId);
    if (!audience || this.isAudienceDisabled(audienceId)) {
      return;
    }

    audience.selected = !audience.selected;
    this.syncSelectedAudiencesSubject();
  }

  isAudienceDisabled(audienceId: string): boolean {
    const audience = this.availableAudiences.find(aud => aud.id === audienceId);
    const currentSelected = this.availableAudiences.filter(aud => aud.selected);

    if (audience?.selected && currentSelected.length <= 2) {
      return true;
    }

    if (!audience?.selected && currentSelected.length >= 5) {
      return true;
    }

    return false;
  }

  setDateRange(range: '7days' | '14days' | '30days' | '90days' | 'year'): void {
    this.selectedDateRangeSubject.next(range);
  }

  getSelectedDateRange(): '7days' | '14days' | '30days' | '90days' | 'year' {
    return this.selectedDateRangeSubject.value;
  }

  getAvailableDevices(): Array<{ id: string; name: string; selected: boolean }> {
    return this.availableDevices;
  }

  toggleDevice(deviceId: string): void {
    const device = this.availableDevices.find(dev => dev.id === deviceId);
    if (!device) return;

    device.selected = !device.selected;
    this.syncSelectedDevices();
  }

  getSelectedDevices(): Array<{ id: string; name: string; selected: boolean }> {
    return this.availableDevices.filter(dev => dev.selected);
  }

  getAvailableTrafficSources(): Array<{ id: string; name: string; selected: boolean }> {
    return this.availableTrafficSources;
  }

  toggleTrafficSource(sourceId: string): void {
    const source = this.availableTrafficSources.find(src => src.id === sourceId);
    if (!source) return;

    source.selected = !source.selected;
    this.syncSelectedSources();
  }

  getSelectedTrafficSources(): Array<{ id: string; name: string; selected: boolean }> {
    return this.availableTrafficSources.filter(src => src.selected);
  }

  getAvailableGoals(): Array<{ id: string; name: string; selected: boolean }> {
    return this.availableGoals;
  }

  getSelectedGoal(): { id: string; name: string; selected: boolean } | null {
    return this.availableGoals.find(goal => goal.selected) || null;
  }

  selectGoal(goalId: string): void {
    const current = this.availableGoals.find(goal => goal.selected);

    if (current && current.id === goalId) {
      current.selected = false;
      this.selectedGoalSubject.next('');
      return;
    }

    this.availableGoals.forEach(goal => (goal.selected = false));

    const goal = this.availableGoals.find(g => g.id === goalId);
    if (goal) {
      goal.selected = true;
      this.selectedGoalSubject.next(goalId);
    }
  }

  clearNonAudienceFilters(): void {
    if (this.selectedDevicesSubject.value.length > 0) {
      this.availableDevices.forEach(dev => (dev.selected = false));
      this.selectedDevicesSubject.next([]);
    }

    if (this.selectedTrafficSourcesSubject.value.length > 0) {
      this.availableTrafficSources.forEach(src => (src.selected = false));
      this.selectedTrafficSourcesSubject.next([]);
    }

    if (this.selectedGoalSubject.value) {
      this.availableGoals.forEach(goal => (goal.selected = false));
      this.selectedGoalSubject.next('');
    }
  }

  getApplyFiltersToAllCharts(): boolean {
    return this.applyFiltersToAllChartsSubject.value;
  }

  setApplyFiltersToAllCharts(_value: boolean): void {
    const normalized = this.defaultApplyFiltersState;
    if (this.applyFiltersToAllChartsSubject.value !== normalized) {
      this.applyFiltersToAllChartsSubject.next(normalized);
    }
    this.persistApplyFiltersPreference(normalized);
  }

  toggleApplyFiltersToAllCharts(): void {
    this.setApplyFiltersToAllCharts(this.defaultApplyFiltersState);
  }

  areFiltersLoaded(): boolean {
    return this.filtersLoadedSubject.value;
  }

  private syncSelectedAudiences(): void {
    const selectedIds = this.availableAudiences
      .filter(aud => aud.selected)
      .map(aud => aud.id);
    this.selectedAudiencesSubject.next([...selectedIds]);
  }

  private syncSelectedAudiencesSubject(): void {
    const selectedIds = this.availableAudiences.filter(aud => aud.selected).map(aud => aud.id);
    this.selectedAudiencesSubject.next(selectedIds);
  }

  private syncSelectedDevices(): void {
    const selectedDeviceIds = this.availableDevices.filter(dev => dev.selected).map(dev => dev.id);
    if (JSON.stringify(selectedDeviceIds.sort()) !== JSON.stringify(this.selectedDevicesSubject.value.slice().sort())) {
      this.selectedDevicesSubject.next(selectedDeviceIds);
    }
  }

  private syncSelectedSources(): void {
    const selectedSourceIds = this.availableTrafficSources.filter(src => src.selected).map(src => src.id);
    if (JSON.stringify(selectedSourceIds.sort()) !== JSON.stringify(this.selectedTrafficSourcesSubject.value.slice().sort())) {
      this.selectedTrafficSourcesSubject.next(selectedSourceIds);
    }
  }

  private syncSelectedGoal(): void {
    const selectedGoal = this.availableGoals.find(goal => goal.selected);
    if (selectedGoal && selectedGoal.id !== this.selectedGoalSubject.value) {
      this.selectedGoalSubject.next(selectedGoal.id);
    }
  }

  private loadApplyFiltersPreference(): boolean {
    return this.defaultApplyFiltersState;
  }

  private persistApplyFiltersPreference(value: boolean): void {
    try {
      localStorage.setItem(this.applyFiltersStorageKey, value ? 'true' : 'false');
    } catch {
      // ignore persistence failures
    }
  }

  private isReachRoute(path: string): boolean {
    if (!path) {
      return false;
    }
    return path.startsWith('/reach');
  }
}
